<template>
  <div>
    <b-card class="zoho-sign-card">
      <div class="section-header">
        <feather-icon
          icon="FileTextIcon"
          size="20"
          class="section-icon"
        />
        <h5 class="section-title">
          Zoho Sign Verträge
        </h5>
      </div>

      <!-- Contract Status Overview -->
      <div class="contract-overview">
        <div class="overview-stats">
          <div class="stat-item">
            <div class="stat-number">
              {{ contractStats.total }}
            </div>
            <div class="stat-label">
              Verträge gesamt
            </div>
          </div>
          <div class="stat-item">
            <div class="stat-number">
              {{ contractStats.sent }}
            </div>
            <div class="stat-label">
              Versendet
            </div>
          </div>
          <div class="stat-item">
            <div class="stat-number">
              {{ contractStats.signed }}
            </div>
            <div class="stat-label">
              Unterzeichnet
            </div>
          </div>
        </div>
      </div>

      <!-- Contract Details Table -->
      <div
        v-if="hasZohoData"
        class="contracts-table"
      >
        <b-table
          :items="contractItems"
          :fields="fields"
          striped
          hover
          responsive
          show-empty
          empty-text="Keine Vertragsdaten verfügbar"
        >
          <template #cell(status)="data">
            <b-badge
              :variant="getStatusVariant(data.item.status)"
              class="status-badge"
            >
              {{ getStatusLabel(data.item.status) }}
            </b-badge>
          </template>

          <template #cell(sentDate)="data">
            {{ data.item.sentDate ? getFormattedDate(data.item.sentDate) : '-' }}
          </template>

          <template #cell(actions)="data">
            <div class="action-buttons">
              <b-button
                v-if="data.item.documentId"
                variant="primary"
                size="sm"
                :href="data.item.zohoUrl"
                target="_blank"
                class="action-btn"
              >
                <feather-icon
                  icon="ExternalLinkIcon"
                  size="14"
                />
                Zoho öffnen
              </b-button>
            </div>
          </template>
        </b-table>
      </div>

      <!-- No Data State -->
      <div
        v-else
        class="no-data-state"
      >
        <feather-icon
          icon="FileMinusIcon"
          size="48"
          class="no-data-icon"
        />
        <h6 class="no-data-title">
          {{ noDataTitle }}
        </h6>
        <p class="no-data-text">
          {{ noDataMessage }}
        </p>
      </div>
    </b-card>
  </div>
</template>

<script>
import {
  BCard,
  BTable,
  BBadge,
  BButton,
} from 'bootstrap-vue'
import helper from '@/helper/helper'
import zohoSignHelper from '@/helper/zohoSignHelper'

export default {
  setup() {
    const { getFormattedDate } = helper()
    const {
      getOfferContracts,
      getOfferContractStats,
      hasContracts,
      getStatusVariant,
      getStatusLabel
    } = zohoSignHelper()
    return {
      getFormattedDate,
      getOfferContracts,
      getOfferContractStats,
      hasContracts,
      getStatusVariant,
      getStatusLabel
    }
  },
  components: {
    BCard,
    BTable,
    BBadge,
    BButton,
  },
  props: {
    offerEntry: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      fields: [
        {
          key: 'storageIdent',
          label: 'Lager ID',
          sortable: true,
        },
        {
          key: 'status',
          label: 'Status',
          sortable: true,
        },
        {
          key: 'documentId',
          label: 'Dokument ID',
          sortable: false,
        },
        {
          key: 'requestId',
          label: 'Request ID',
          sortable: false,
        },
        {
          key: 'sentDate',
          label: 'Versendet am',
          sortable: true,
        },
        {
          key: 'recipientEmail',
          label: 'Empfänger',
          sortable: false,
        },
        {
          key: 'actions',
          label: 'Aktionen',
          sortable: false,
        },
      ],
    }
  },
  computed: {
    hasZohoData() {
      return this.contractItems.length > 0
    },
    contractItems() {
      if (!this.offerEntry || !this.offerEntry.id) {
        console.log('- No offer ID available')
        return []
      }

      // Get contracts from ZohoContracts table
      const zohoContracts = this.$store.getters['zohoContract/zohoContracts'] || []
      const storages = this.offerEntry.storages || []

      console.log('=== OffersViewZohoSignTable DEBUG (ZohoContracts) ===')
      console.log('- Offer ID:', this.offerEntry.id)
      console.log('- Total ZohoContracts in store:', zohoContracts.length)
      console.log('- Offer storages:', storages.map(s => ({ id: s.id, storageIdent: s.storageIdent })))
      console.log('- Contracts for this offer:', zohoContracts.filter(c =>
        c.offer === this.offerEntry.id || c.offer?.id === this.offerEntry.id).map(c => ({
        id: c.id,
        storage: c.storage,
        documentId: c.zohoDocumentId,
        status: c.zohoSignStatus
      })))

      // Use centralized helper
      const contracts = this.getOfferContracts(this.offerEntry.id, zohoContracts, storages)
      console.log('- Final contractItems count:', contracts.length)

      return contracts
    },
    contractStats() {
      if (!this.offerEntry || !this.offerEntry.id) {
        return { total: 0, sent: 0, signed: 0 }
      }

      const zohoContracts = this.$store.getters['zohoContract/zohoContracts'] || []
      const storages = this.offerEntry.storages || []

      return this.getOfferContractStats(this.offerEntry.id, zohoContracts, storages)
    },
    noDataTitle() {
      return 'Keine Zoho Sign Verträge'
    },
    noDataMessage() {
      return 'Für dieses Angebot wurden noch keine Verträge über Zoho Sign versendet. Verträge werden automatisch hier angezeigt, sobald sie erstellt wurden.'
    },
  },
  methods: {
    // Helper methods are imported from zohoSignHelper in setup()
  },
}
</script>

<style lang="scss" scoped>
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');

* {
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

.zoho-sign-card {
  border: 1px solid #e9ecef;
  border-radius: 16px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.04);
  transition: all 0.3s ease;

  &:hover {
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
    transform: translateY(-2px);
  }

  .card-body {
    padding: 2rem;
  }
}

.section-header {
  display: flex;
  align-items: center;
  margin-bottom: 1.5rem;

  .section-icon {
    color: #007bff;
    margin-right: 0.75rem;
  }

  .section-title {
    font-size: 1.25rem;
    font-weight: 600;
    color: #2c3e50;
    margin: 0;
  }
}

.contract-overview {
  margin-bottom: 2rem;
  padding: 1.5rem;
  background: #f8f9fa;
  border-radius: 12px;
  border-left: 4px solid #007bff;

  .overview-stats {
    display: flex;
    justify-content: space-around;
    text-align: center;

    .stat-item {
      .stat-number {
        font-size: 2rem;
        font-weight: 700;
        color: #2c3e50;
        line-height: 1;
      }

      .stat-label {
        font-size: 0.875rem;
        color: #8a92b2;
        margin-top: 0.5rem;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        font-weight: 500;
      }
    }
  }
}

.contracts-table {
  .action-buttons {
    display: flex;
    gap: 0.5rem;
    flex-wrap: wrap;

    .action-btn {
      display: flex;
      align-items: center;
      gap: 0.25rem;
      padding: 0.375rem 0.75rem;
      font-size: 0.875rem;
      border-radius: 6px;
      transition: all 0.2s ease;

      &:hover {
        transform: translateY(-1px);
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
      }
    }
  }

  .status-badge {
    font-size: 0.75rem;
    padding: 0.375rem 0.75rem;
    border-radius: 12px;
    font-weight: 500;
  }
}

.no-data-state {
  text-align: center;
  padding: 3rem 2rem;

  .no-data-icon {
    color: #adb5bd;
    margin-bottom: 1rem;
  }

  .no-data-title {
    color: #495057;
    font-weight: 600;
    margin-bottom: 0.75rem;
  }

  .no-data-text {
    color: #8a92b2;
    font-size: 0.95rem;
    line-height: 1.5;
    max-width: 400px;
    margin: 0 auto;
  }
}

::v-deep .table {
  td, th {
    border-top: 1px solid #f1f3f4;
    vertical-align: middle;
  }

  thead th {
    border-bottom: 2px solid #dee2e6;
    background: #f8f9fa;
    font-weight: 600;
    color: #495057;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    font-size: 0.875rem;
  }

  tbody tr:hover {
    background-color: #f8f9fa;
  }
}
</style>
